import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";

import { Modal, ButtonContainer, Button, Loader, APIErrorMessage } from "shared/components";
import { Spacer } from "shared/layout";
import { setMessageModal } from "shared/components/MessageModal";
import { BoldText } from "shared/styles";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { useVenuePageContext } from "Venues/contexts";
import { dataMetaSelector, dispatchGetVenueAction, publishVenue } from "Venues/state";
import { VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

export interface PublishModalProps {
  handleClose: () => void;
}

export const PublishModal: FC<PublishModalProps> = ({ handleClose }) => {
  const { venueId, venue } = useVenuePageContext();
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.publishVenue));
  const dispatch = useDispatch();

  useClearActionMetaOnUnMount(VENUE_ACTIONS.publishVenue);

  const handlePublish = () => {
    dispatch(publishVenue(venueId));
  };

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        setMessageModal({
          message: { body: copy.publishVenue.success, status: IStatus.success },
        })
      );
      // refetch venue to update permissions
      dispatchGetVenueAction(dispatch, venueId);
      handleClose();
    }
  }, [apiMeta, venueId, dispatch, handleClose]);

  if (!venue) return null;

  return (
    <Modal handleClose={handleClose} dataTestId="publish_modal">
      <Spacer size="m">
        <div>
          {copy.publishVenue.message} <BoldText>{venue.title}</BoldText>?
        </div>
        <ButtonContainer>
          <Button onClick={handlePublish} data-testid="publish_button">
            <Spacer spaceDirection="horizontal" size="xs">
              <span>{copy.publishVenue.submit}</span>
              {apiMeta?.status === IAPIState.pending && <Loader size="s" />}
            </Spacer>
          </Button>
          <Button
            onClick={handleClose}
            theme={ITheme.secondary}
            data-testid="cancel_publish_button"
          >
            {copy.form.cancel}
          </Button>
        </ButtonContainer>
        {!!apiMeta?.error && <APIErrorMessage apiErrorMessage={true} apiError={apiMeta?.error} />}
      </Spacer>
    </Modal>
  );
};
